import React, { useState, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import SettingsPage from './components/SettingsPage';
import JobsPage from './components/JobsPage.jsx';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPage, setCurrentPage] = useState('settings');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const [settings, setSettings] = useState({
    upPeriod: 720,
    startPeriod: 60, // 60 minutes default
    url: 'https://api.example.com',
    airtimeUnitPrice: 1.00, // Starting from 1
    gasLevel: 10, // 10 kg out of max 13 kg
    currentTime: new Date().toLocaleTimeString(),
    cardId: '', // Optional
    customerId: 'CUST001',
    cylinderSerial: 'CYL123456',
    unitPrice: 2.50,
    batteryModel: 'Li-Ion 3.7V',
    batterySerial: 'BAT789012',
    warningLowBattery: 20, // 20% threshold
    warningLowCredit: 5.00 // $5.00 threshold
  });

  const [jobs, setJobs] = useState([
    { id: '1', name: 'Update Settings', description: 'Update and configure system settings', selected: false },
    { id: '2', name: 'Activate', description: 'Activate system and start monitoring operations', selected: false },
    { id: '3', name: 'Cylinder Install', description: 'Install or configure gas cylinder parameters', selected: false },
    { id: '4', name: 'Adjust Credit', description: 'Modify credit balance and billing parameters', selected: false },
    { id: '5', name: 'Cooking Sessions', description: 'Modify credit balance and billing parameters', selected: false },
    { id: '6', name: 'Child Lock', description: 'Enable or configure child safety lock features', selected: false },
    { id: '7', name: 'Change Battery', description: 'Update battery status and replacement indicators', selected: false },
    { id: '8', name: 'Get Cumulative Report', description: 'Generate cumulative usage and status reports', selected: false },
    { id: '9', name: 'Get Hardware Status', description: 'Retrieve hardware status and information', selected: false },
    { id: '10', name: 'Get Settings', description: 'Fetch current system settings and configuration', selected: false },
    { id: '11', name: 'Get Meter Status', description: 'Check current meter operational status and diagnostics', selected: false },
    { id: '12', name: 'Get Logs', description: 'Retrieve system operation and error logs', selected: false },
    { id: '13', name: 'Open Meter Lock', description: 'Unlock meter for maintenance or configuration', selected: false },
    { id: '14', name: 'Adjust Data', description: 'Calibrate and adjust sensor data parameters', selected: false },
    { id: '15', name: 'Deactivate', description: 'Deactivate system and stop monitoring operations', selected: false },
    { id: '16', name: 'Reset All', description: 'Reset all system parameters to default values', selected: false },
    { id: '17', name: 'OTA', description: 'Over-the-air firmware update and system upgrade', selected: false }
  ]);

  const [selectAllActive, setSelectAllActive] = useState(false);

  // Generate mock large content for log files
  const generateLogContent = (logType, jobName) => {
    const entries = [];
    const baseEntries = 50; // Reduced from 1000 for performance, but still substantial

    for (let i = 0; i < baseEntries; i++) {
      const timestamp = new Date(Date.now() - i * 60000).toISOString();
      const levels = ['INFO', 'WARNING', 'ERROR'];
      const level = levels[Math.floor(Math.random() * levels.length)];

      let messages = [
        `${logType} sensor reading: ${Math.random().toFixed(2)}`,
        `${logType} temperature: ${(20 + Math.random() * 30).toFixed(1)}°C`,
        `${logType} pressure: ${(100 + Math.random() * 50).toFixed(2)} kPa`,
        `${logType} communication status: OK`,
        `${logType} memory usage: ${(Math.random() * 100).toFixed(1)}%`,
        `${logType} processing request: ${Math.floor(Math.random() * 100)}`,
      ];

      // Add job-specific messages if job name is provided
      if (jobName) {
        messages = [
          `${logType} executing job: ${jobName}`,
          `${logType} job ${jobName} - status: processing`,
          `${logType} job ${jobName} - validation successful`,
          `${logType} job ${jobName} - operation completed`,
          ...messages
        ];
      }

      const message = messages[Math.floor(Math.random() * messages.length)];
      entries.push(`[${timestamp}] [${level}] ${message}`);
    }
    return entries.join('\n');
  };

  const [meterLogFiles, setMeterLogFiles] = useState([
    {
      id: 'esp32-logs',
      name: 'ESP32 Logs',
      type: 'esp32',
      logs: [], // Start with empty logs - populated on request
      fullContent: '', // Will be generated when needed
      lastUpdated: 'No data available'
    },
    {
      id: 'stm-logs',
      name: 'STM Logs',
      type: 'stm',
      logs: [], // Start with empty logs - populated on request
      fullContent: '', // Will be generated when needed
      lastUpdated: 'No data available'
    }
  ]);

  const [meterNumber, setMeterNumber] = useState('');

  // Simulate connection status changes
  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionStatus(prev => prev === 'connected' ? 'disconnected' : 'connected');
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Update current time
  useEffect(() => {
    const interval = setInterval(() => {
      setSettings(prev => ({
        ...prev,
        currentTime: new Date().toLocaleTimeString()
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleLogin = (username, password) => {
    // Mock authentication - accept any non-empty credentials
    if (username.trim() && password.trim()) {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentPage('settings');
  };

  const updateJobSelection = (jobId, selected) => {
    setJobs(prev => prev.map(job =>
      job.id === jobId ? { ...job, selected } : job
    ));

    // If deselecting any job, deselect select all
    if (!selected && selectAllActive) {
      setSelectAllActive(false);
    }

    // If selecting all jobs manually, activate select all
    const updatedJobs = jobs.map(job =>
      job.id === jobId ? { ...job, selected } : job
    );
    if (selected && updatedJobs.every(job => job.selected)) {
      setSelectAllActive(true);
    }
  };

  const handleSelectAllChange = (selected) => {
    setSelectAllActive(selected);

    // Select or deselect all jobs
    setJobs(prev => prev.map(job => ({ ...job, selected })));
  };

  const executeSelectedJobs = (confirmedSettings) => {
    const selectedJobs = jobs.filter(job => job.selected);

    if (selectedJobs.length === 0) return;

    // Update settings with confirmed values
    setSettings(confirmedSettings);

    // Generate logs for executed jobs
    selectedJobs.forEach(job => {
      // Randomly choose which log file to add to (or both)
      const targetLogs = Math.random() > 0.5 ? ['esp32-logs'] : ['stm-logs'];
      if (Math.random() > 0.7) {
        targetLogs.push(targetLogs[0] === 'esp32-logs' ? 'stm-logs' : 'esp32-logs');
      }

      targetLogs.forEach(logFileId => {
        const logFile = meterLogFiles.find(file => file.id === logFileId);
        if (!logFile) return;

        // Create multiple log entries for the job execution
        const jobLogs = [];
        const logCount = Math.floor(Math.random() * 3) + 2; // 2-4 logs per job

        for (let i = 0; i < logCount; i++) {
          const level = Math.random() > 0.8 ? 'warning' :
            Math.random() > 0.95 ? 'error' : 'info';
          const phases = ['initiated', 'processing', 'validating', 'completed'];
          const phase = phases[Math.min(i, phases.length - 1)];

          jobLogs.push({
            id: `${Date.now()}-${job.id}-${i}`,
            timestamp: new Date(Date.now() + i * 1000).toLocaleString(),
            level,
            message: `Job "${job.name}" ${phase} - ${level === 'error' ? 'Error occurred' :
              level === 'warning' ? 'Warning detected' :
                'Operation successful'}`
          });
        }

        setMeterLogFiles(prev => prev.map(file =>
          file.id === logFileId
            ? {
              ...file,
              logs: [...jobLogs, ...file.logs].slice(0, 20), // Keep last 20 logs
              fullContent: generateLogContent(file.type.toUpperCase(), job.name),
              lastUpdated: new Date().toLocaleString()
            }
            : file
        ));
      });
    });

    // Reset selections
    setJobs(prev => prev.map(job => ({ ...job, selected: false })));
    setSelectAllActive(false);
  };

  const handleRequestLogs = (logFileId) => {
    // Generate logs when specifically requested
    setMeterLogFiles(prev => prev.map(file =>
      file.id === logFileId
        ? {
          ...file,
          fullContent: generateLogContent(file.type.toUpperCase()),
          lastUpdated: new Date().toLocaleString()
        }
        : file
    ));
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {currentPage === 'settings' ? (
        <SettingsPage
          settings={settings}
          onSettingsChange={setSettings}
          onNavigateToJobs={() => setCurrentPage('jobs')}
          onLogout={handleLogout}
        />
      ) : (
        <JobsPage
          jobs={jobs}
          meterLogFiles={meterLogFiles}
          connectionStatus={connectionStatus}
          meterNumber={meterNumber}
          selectAllActive={selectAllActive}
          settings={settings}
          onMeterNumberChange={setMeterNumber}
          onJobSelectionChange={updateJobSelection}
          onSelectAllChange={handleSelectAllChange}
          onExecuteJobs={executeSelectedJobs}
          onRequestLogs={handleRequestLogs}
          onNavigateToSettings={() => setCurrentPage('settings')}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}